import { useAuthenticator } from '@aws-amplify/ui-react';
import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePatchUser from '../../../../API/hooks/users/usePatchUser';
import { PatchUserRequest, User } from '../../../../API/types/Users';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import {
  emailValidator, fullNameValidator,
} from '../../../../utils/formUtils';
import S from './EditUserStyles';
import Select from '../../../../components/common/Select/Select';
import useGetRole from '../../../../API/hooks/useGetRole';

type Props = {
  data: User | undefined;
  toggleModal: (closed: boolean) => void;
};

function EditUser({ data, toggleModal }: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);
  const { user: { username, attributes }, signOut } = useAuthenticator();
  const {
    isFleetViewer, isFleetManager, isSupport, isAdmin, isFleetUser,
  } = useGetRole();
  const myself = data?.email === attributes?.email;

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePatchUser(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      name: data?.name || '',
      email: data?.email || '',
      role: data?.role || 'fleetViewer',
    },
    onSubmit: (values) => mutate({
      name: values.name,
      email: undefined,
      role: !isFleetViewer && values.role !== data?.role ? values.role : undefined,
      fleetId: data?.fleetId || '',
      id: data?._id || '',
      adminRequest: isAdmin || isSupport,
    } as unknown as PatchUserRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.name.length) errors.name = 'Required';
      else if (values.name.length < 2) errors.name = 'Name must be at least 2 characters';
      else if (values.name.length > 128) errors.name = 'Name must be 2 to 128 characters';
      // if (values.name.length && !fullNameValidator.test(values.name)) {
      //   errors.name = 'Name should contain only letters';
      // }
      if (!values.email.length) errors.email = 'Required';
      if (values.email.length && !emailValidator.test(values.email)) {
        errors.email = 'Invalid email';
      }
      if (!values.role) errors.role = 'Required';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleSelectChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <Input
              label="Name"
              name="name"
              placeholder="Type user name"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              error={formik.touched.name && formik.errors.name}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Email"
              name="email"
              placeholder="Type user email"
              type="email"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.touched.email && formik.errors.email}
              disabled
            />
          </S.RowContainer>
          <S.RowContainer>
            <p>Select role:</p><Select
              value={formik.values.role}
              name="role"
              simple
              options={[
                {
                  label: 'Fleet Viewer',
                  value: 'fleetViewer',
                },
                {
                  label: 'Fleet Manager',
                  value: 'fleetManager',
                  hidden: isFleetViewer || isFleetManager,
                },
                {
                  label: 'Fleet Admin',
                  value: 'fleetAdmin',
                  hidden: isFleetUser,
                },
              ]}
              onChange={handleSelectChange}
              disabled={myself}
            />
          </S.RowContainer>
          <S.RowContainer className="buttons-container">
            <Button
              height="50px"
              onClick={() => toggleModal(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="add-button"
              height="50px"
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid || !!submitError}
            >
              Save
            </Button>
          </S.RowContainer>
          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default EditUser;
